import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';

import { setTildaBlockFinish, setTildaBlockStart } from '@/store/lms/actions';
import { getTilda } from '@/store/tilda/actions';

import { selectLMS } from '@/store/lms/selectors';

import { Button, Col, Row, Tooltip } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';

import useIsMobile from '@shared/hooks/useIsMobile';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import css from './index.module.scss';

import { FINISHED } from '@/constants/study-plan-statuses';

const INITIAL_ROUTE = '/data/tilda';

export const Tilda = props => {
  const { t } = useTranslation('article');
  const dispatch = useDispatch();

  const { statistic } = useSelector(selectLMS);

  const [homePage, setHomePage] = useState(null);
  const [project, setProject] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [tilda, setTilda] = useState(null);
  const [domain, setDomain] = useState(null);
  const [isMobile] = useIsMobile(768);
  const [isFullscreenMobile, setIsFullscreenMobile] = useState(false);
  const iframeRef = useRef(null);
  const observerRef = useRef(null);

  const next = statistic?.blocksStat && statistic.blocksStat.filter(item => item.status !== FINISHED).length;

  // Функция для замены ссылок в DOM с помощью TreeWalker
  const replaceLinksInDOM = useCallback(
    document => {
      if (!document || !project) return;

      const treeWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
        acceptNode: node => {
          // Проверяем только элементы A с href атрибутом
          if (node.tagName === 'A' && node.href) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        },
      });

      const nodesToProcess = [];

      let currentNode;

      while ((currentNode = treeWalker.nextNode()) !== null) {
        nodesToProcess.push(currentNode);
      }

      // Обрабатываем только ссылки с переходами
      nodesToProcess.forEach(node => {
        if (node.tagName === 'A') {
          const originalHref = node.getAttribute('href');

          // Проверяем, что это относительная ссылка и содержит page####.html
          if (
            originalHref &&
            !originalHref.startsWith('http') &&
            !originalHref.startsWith('mailto:') &&
            !originalHref.startsWith('tel:') &&
            /page\d+\.html/.test(originalHref)
          ) {
            // Заменяем относительные ссылки на абсолютные
            const newHref = `https://${domain}${INITIAL_ROUTE}/${project}${originalHref.startsWith('/') ? originalHref : '/' + originalHref}`;
            node.setAttribute('href', newHref);

            // Добавляем обработчик для предотвращения перехода
            node.addEventListener('click', e => {
              e.preventDefault();
              const href = node.getAttribute('href');
              if (href) {
                const pathParts = href.split('/');
                const fileName = pathParts[pathParts.length - 1];
                if (fileName && fileName !== currentPage) {
                  setCurrentPage(fileName);
                }
              }
            });
          }
        }
      });
    },
    [project, currentPage, setCurrentPage]
  );

  // Отслеживание изменений в iframe
  useEffect(() => {
    const iframe = iframeRef.current;

    if (iframe) {
      const handleLoad = () => {
        const iframeWindow = iframe.contentWindow;
        const iframeDocument = iframe.contentDocument;

        if (iframeWindow && iframeWindow.location) {
          const currentUrl = iframeWindow.location.pathname;

          // Извлекаем только имя файла из полного пути
          const pathParts = currentUrl.split('/');
          const fileName = pathParts[pathParts.length - 1];

          if (fileName && fileName !== currentPage) {
            setCurrentPage(fileName);
          }
        }

        // Заменяем все ссылки в загруженном документе
        if (iframeDocument && iframeDocument.body) {
          replaceLinksInDOM(iframeDocument);
        }
      };

      // Отслеживаем изменения через MutationObserver для обработки динамически добавленного контента
      const setupMutationObserver = () => {
        const iframeDocument = iframe.contentDocument;
        if (iframeDocument) {
          observerRef.current = new MutationObserver(mutations => {
            let shouldUpdateLinks = false;

            mutations.forEach(mutation => {
              if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                // Проверяем, добавлены ли новые элементы с ссылками
                mutation.addedNodes.forEach(node => {
                  if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.tagName === 'A' || node.querySelector('a')) {
                      shouldUpdateLinks = true;
                    }
                  }
                });
              }
            });

            if (shouldUpdateLinks) {
              // Обновляем ссылки в новом контенте
              replaceLinksInDOM(iframeDocument);
            }
          });

          observerRef.current.observe(iframeDocument.body, {
            childList: true,
            subtree: true,
          });
        }
      };

      iframe.addEventListener('load', () => {
        handleLoad();
        setupMutationObserver();
      });

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }
  }, [currentPage, project, homePage, replaceLinksInDOM]);

  useEffect(() => {
    props.getTilda(props.block.tildaId, success => {
      setTilda(success);
    });

    if (props?.currentUser?.domainCompany?.domain) {
      setDomain(props.currentUser.domainCompany.domain);
    }

    if (props?.block && props.block.isAvailableForStart) {
      dispatch(setTildaBlockStart(props.block.blockId, () => props.getStatistic(true)));
    }
  }, []);

  useEffect(() => {
    if (tilda && tilda.pages && typeof tilda.pages === 'object') {
      const trueKey = Object.keys(tilda.pages).find(key => tilda.pages[key] === true);
      const pageKey = trueKey || Object.keys(tilda.pages)?.[0];

      if (pageKey) {
        // Извлекаем project и homePage из строки вида "project###/page###.html"
        const parts = pageKey.split('/');
        if (parts.length === 2) {
          const projectPart = parts[0];
          const homePagePart = parts[1];

          setProject(projectPart);
          setHomePage(homePagePart);
          setCurrentPage(homePagePart); // Устанавливаем начальную страницу
        } else {
          // Если формат не соответствует ожидаемому, используем как есть
          setHomePage(pageKey);
          setCurrentPage(pageKey);
        }
      }
    }
  }, [tilda]);

  const onFinish = () => {
    dispatch(setTildaBlockFinish(props.block.blockId, () => props.getStatistic(next <= 1)));
  };

  const openFullscreen = () => {
    const iframe = document.getElementById('tildaContainer');

    if (!isMobile) {
      const requestMethod = iframe.requestFullscreen;
      if (requestMethod) {
        requestMethod.call(iframe);
      }
    } else {
      setIsFullscreenMobile(!isFullscreenMobile);
    }
  };

  useEffect(() => {
    if (!isMobile && isFullscreenMobile) {
      setIsFullscreenMobile(false);
    }
  }, [isMobile]);

  const validUrl = useMemo(() => {
    if (project && currentPage) {
      // return `${INITIAL_ROUTE}/${project}/${currentPage}`; Для теста локально
      return `https://${domain}${INITIAL_ROUTE}/${project}/${currentPage}`;
    }

    return undefined;
  }, [project, currentPage, domain]);

  const render = () => {
    return (
      <>
        <Row>
          <Col span={24} className={css.tildaWrapper}>
            <div className={css.tildaFullscreenButtonWrapper}>
              <Tooltip title={!isMobile ? t('fullscreen') : ''}>
                <Button
                  className={classNames(
                    css.tildaFullscreenButton,
                    isMobile && isFullscreenMobile && css.tildaFullscreenButton_active
                  )}
                  icon={isMobile && isFullscreenMobile ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={openFullscreen}
                  shape='circle'
                  size='large'
                  type='primary'
                />
              </Tooltip>
            </div>
            <div
              id='tildaContainer'
              className={classNames(
                css.tildaContainer,
                isMobile && isFullscreenMobile && css.tildaFrame_FullscreenMobile
              )}
            >
              {!!validUrl && (
                <iframe
                  ref={iframeRef}
                  allow=''
                  className={classNames(
                    css.tildaFrame,
                    isMobile && isFullscreenMobile && css.tildaFrame_FullscreenMobile
                  )}
                  id='scormIframe'
                  src={validUrl}
                  referrerPolicy='no-referrer'
                  height='100%'
                  width='100%'
                  scrolling='yes'
                  allowFullScreen
                />
              )}
            </div>
          </Col>
        </Row>
        <Row style={{ width: '100%' }}>
          <Col span={24} align='center'>
            <div className={css.tildaButtons}>
              {props.block.status !== 'FINISHED' && (
                <Button onClick={onFinish} type='primary' size='large' style={{ marginRight: 20 }}>
                  {next > 1 ? t('next') : t('end')}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </>
    );
  };

  return render();
};

Tilda.propTypes = {
  currentUser: PropTypes.object.isRequired,
};

const mapActionsToProps = {
  getTilda,
};

export default connect(null, mapActionsToProps)(Tilda);
